import type { Me, PermissionKey } from '@igoal/shared';
import { ApiClientError } from '@igoal/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { api } from './api';
import { setCurrency, setTimezone } from './format';

interface AuthState {
  me: Me | null;
  loading: boolean;
  can: (...keys: PermissionKey[]) => boolean;
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.auth.me();
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const me = meQuery.data ?? null;
  useEffect(() => setTimezone(me?.timezone), [me?.timezone]);
  useEffect(() => setCurrency(me?.currency), [me?.currency]);
  const perms = useMemo(() => new Set(me?.permissions ?? []), [me]);
  const can = useCallback((...keys: PermissionKey[]) => keys.every((k) => perms.has(k)), [perms]);

  // drop cached data from the previous user, but keep the observed ['me'] entry
  const clearExceptMe = useCallback(() => qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' }), [qc]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.auth.login({ email, password, platform: 'web', deviceName: browserName() });
      clearExceptMe();
      qc.setQueryData(['me'], res.me);
      return res.me;
    },
    [qc, clearExceptMe],
  );

  const logout = useCallback(async () => {
    await api.auth.logout().catch(() => undefined);
    clearExceptMe();
    qc.setQueryData(['me'], null);
  }, [qc, clearExceptMe]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  return (
    <AuthContext.Provider value={{ me, loading: meQuery.isLoading, can, login, logout, refresh }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

function browserName() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}
